import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, Card, CardContent, Badge, Spinner } from "@learn-me/ui";
import { Plus, Edit, Trash2, Eye, EyeOff, GraduationCap } from "lucide-react";
import toast from "react-hot-toast";
import { OFFERING_CATEGORY_LABELS } from "@learn-me/types";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { offeringsApi } from "@/features/offerings/api/offeringsApi";

export default function OfferingsPage() {
  const queryClient = useQueryClient();
  const [toDelete, setToDelete] = useState<{
    id: string;
    title: string;
  } | null>(null);

  const { data: offerings, isLoading } = useQuery({
    queryKey: ["admin-offerings"],
    queryFn: () => offeringsApi.listAll(),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["admin-offerings"] });
    queryClient.invalidateQueries({ queryKey: ["offerings"] });
  };

  const deleteMutation = useMutation({
    mutationFn: (id: string) => offeringsApi.remove(id),
    onSuccess: () => {
      invalidate();
      toast.success("Programa eliminado");
      setToDelete(null);
    },
    onError: () => {
      toast.error("Error al eliminar el programa");
      setToDelete(null);
    },
  });

  const publishMutation = useMutation({
    mutationFn: ({ id, isPublished }: { id: string; isPublished: boolean }) =>
      offeringsApi.update(id, { isPublished }),
    onSuccess: (_data, variables) => {
      invalidate();
      toast.success(variables.isPublished ? "Publicado" : "Despublicado");
    },
    onError: () => toast.error("No se pudo cambiar la publicación"),
  });

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Programas y capacitaciones</h1>
          <p className="text-sm text-muted-foreground">
            Oferta formativa institucional que se cotiza por solicitud
          </p>
        </div>
        <Link to="/admin/programas/new">
          <Button>
            <Plus className="h-4 w-4 mr-2" />
            Nuevo programa
          </Button>
        </Link>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner size="lg" />
        </div>
      ) : !offerings || offerings.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <GraduationCap className="h-10 w-10 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground mb-6">
              Todavía no hay programas ni capacitaciones creados.
            </p>
            <Link to="/admin/programas/new">
              <Button>Crear el primero</Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {offerings.map((offering) => (
            <Card key={offering.id}>
              <CardContent className="p-4 flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <Badge variant="outline">
                      {OFFERING_CATEGORY_LABELS[offering.category]}
                    </Badge>
                    <Badge
                      variant={offering.isPublished ? "default" : "secondary"}
                    >
                      {offering.isPublished ? "Publicado" : "Borrador"}
                    </Badge>
                  </div>
                  <h2 className="font-semibold truncate">{offering.title}</h2>
                  <p className="text-sm text-muted-foreground">
                    {offering.durationLabel} · {offering.modalities.join(" · ")}
                  </p>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    title={offering.isPublished ? "Despublicar" : "Publicar"}
                    onClick={() =>
                      publishMutation.mutate({
                        id: offering.id,
                        isPublished: !offering.isPublished,
                      })
                    }
                  >
                    {offering.isPublished ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                  <Link to={`/admin/programas/${offering.id}/edit`}>
                    <Button variant="ghost" size="sm" title="Editar">
                      <Edit className="h-4 w-4" />
                    </Button>
                  </Link>
                  <Button
                    variant="ghost"
                    size="sm"
                    title="Eliminar"
                    onClick={() =>
                      setToDelete({ id: offering.id, title: offering.title })
                    }
                  >
                    <Trash2 className="h-4 w-4 text-red-600" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <ConfirmDialog
        isOpen={!!toDelete}
        title="Eliminar programa"
        description={`¿Seguro que desea eliminar "${toDelete?.title}"? Las solicitudes ya recibidas se conservan, pero dejan de estar ligadas a este programa.`}
        confirmText="Eliminar"
        variant="danger"
        isLoading={deleteMutation.isPending}
        onConfirm={() => toDelete && deleteMutation.mutate(toDelete.id)}
        onClose={() => setToDelete(null)}
      />
    </div>
  );
}
